import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, ArrowDownRight, Scale, Activity, TrendingUp, TrendingDown, FileDown } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { exportNodeToPDF, bizSlug } from "@/lib/pdfPage";
import { PrintHeader } from "@/components/PrintHeader";
import { toast } from "sonner";

function Kpi({ overline, value, sub, accent, testid, icon: Icon }) {
  return (
    <div className="border border-[#E3E3E3] bg-white p-5 fade-up" data-testid={testid}>
      <div className="flex items-start justify-between mb-4">
        <div className="overline">{overline}</div>
        <Icon className="h-4 w-4 text-[#6B7280]" />
      </div>
      <div className={`font-display text-2xl font-bold tracking-tight ${accent || "text-[#0F172A]"}`}>{value}</div>
      {sub && <div className="text-[11px] text-[#6B7280] mt-2 leading-snug">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { isEditor, fmt, business } = useAuth();
  const formatPKR = fmt;
  const [summary, setSummary] = useState(null);
  const [dues, setDues] = useState(null);
  const [exporting, setExporting] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setSummary(r.data));
    api.get("/reports/dues").then((r) => setDues(r.data));
  }, []);

  const handleExportPDF = async () => {
    if (!printRef.current) return;
    setExporting(true);
    try {
      const date = new Date().toISOString().slice(0, 10);
      const filename = `${bizSlug(business?.name)}-dashboard-${date}.pdf`;
      await exportNodeToPDF(printRef.current, filename);
      toast.success("Dashboard PDF downloaded");
    } catch (e) {
      toast.error("Failed to generate PDF");
    } finally {
      setExporting(false);
    }
  };

  if (!summary) {
    return <div className="p-10 text-sm text-[#6B7280]" data-testid="dashboard-loading">Loading…</div>;
  }

  const topDues = (dues?.rows || []).slice(0, 5);

  return (
    <div className="p-10 max-w-7xl" data-testid="dashboard-page">
      <PageHeader overline="Overview" title="Dashboard" testid="dashboard-header">
        <div className="flex items-center gap-2">
          <Button onClick={handleExportPDF} disabled={exporting} variant="outline"
            data-testid="dashboard-download-pdf"
            className="rounded-none h-10 text-xs uppercase tracking-wider font-semibold border-[#0F172A]">
            <FileDown className="h-3.5 w-3.5 mr-2" /> {exporting ? "Generating…" : "Download PDF"}
          </Button>
          {isEditor && (
            <Link to="/ledger" data-testid="dashboard-add-entry-link"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#0F172A] hover:bg-[#1E293B] text-white text-xs font-semibold uppercase tracking-wider transition-all">
              + New Entry
            </Link>
          )}
        </div>
      </PageHeader>

      <div ref={printRef} className="bg-white" data-testid="dashboard-print-region">
        {/* Print-only header: shown in PDF, hidden on screen */}
        <div style={{ display: "none" }} className="mb-4" data-pdf-only="true">
          <PrintHeader business={business} title="Dashboard" />
        </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-[10px] mb-[10px]">
        <Kpi overline="Net Balance" value={formatPKR(summary.balance)}
             sub={summary.balance >= 0 ? "Healthy cash position" : "Negative cash flow"}
             accent={summary.balance >= 0 ? "text-[#0F172A]" : "text-[#EF4444]"}
             icon={Scale} testid="kpi-balance" />
        <Kpi overline="Total Credit" value={formatPKR(summary.total_credit)}
             sub={`${summary.credit_count} inflow entries`} accent="text-[#10B981]"
             icon={ArrowUpRight} testid="kpi-credit" />
        <Kpi overline="Total Debit" value={formatPKR(summary.total_debit)}
             sub={`${summary.debit_count} outflow entries`} accent="text-[#EF4444]"
             icon={ArrowDownRight} testid="kpi-debit" />
        <Kpi overline="Activity" value={summary.credit_count + summary.debit_count}
             sub="Total entries recorded" icon={Activity} testid="kpi-activity" />
      </div>

      {/* Receivables & Payables row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-[10px] mb-[10px]">
        <Kpi overline="Receivable" value={formatPKR(summary.total_receivable || 0)}
             sub="Total amount owed to you"
             accent="text-[#2563EB]"
             icon={TrendingUp} testid="kpi-receivable" />
        <Kpi overline="Payable" value={formatPKR(summary.total_payable || 0)}
             sub="Total amount you owe"
             accent="text-[#EF4444]"
             icon={TrendingDown} testid="kpi-payable" />
        <Kpi overline="Net Dues" value={formatPKR(summary.net_dues || 0)}
             sub={(summary.net_dues || 0) >= 0 ? "Net amount owed to you" : "Net amount you owe"}
             accent={(summary.net_dues || 0) >= 0 ? "text-[#2563EB]" : "text-[#EF4444]"}
             icon={Scale} testid="kpi-net-dues" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-[10px]">
        {/* Recent activity */}
        <div className="border border-[#E3E3E3] bg-white">
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#E3E3E3]">
            <div>
              <div className="overline mb-1">Recent Activity</div>
              <h3 className="font-display text-lg font-semibold text-[#0F172A]">Latest 5 entries</h3>
            </div>
            <Link to="/ledger" data-testid="dashboard-view-all-link" className="text-xs uppercase tracking-wider font-semibold text-[#0F172A] border-b border-[#0F172A]">
              View all →
            </Link>
          </div>
          <div className="divide-y divide-[#E3E3E3]" data-testid="recent-entries-list">
            {summary.recent.length === 0 && (
              <div className="px-6 py-12 text-center text-sm text-[#6B7280]" data-testid="recent-empty">
                No entries yet. Start by adding your first credit or debit.
              </div>
            )}
            {summary.recent.map((it) => (
              <div key={it.id} className="px-6 py-4 flex items-center justify-between" data-testid={`recent-row-${it.id}`}>
                <div className="flex items-center gap-4">
                  <div className={`w-1 h-10 ${it.type === "credit" ? "bg-[#10B981]" : "bg-[#EF4444]"}`} />
                  <div>
                    <div className="text-sm font-medium text-[#111827]">{it.description || "—"}</div>
                    <div className="text-xs text-[#6B7280]">
                      {it.date} · {it.party_name || "No party"} · {it.category_name || "Uncategorized"}
                    </div>
                  </div>
                </div>
                <div className={`font-mono text-sm font-semibold ${it.type === "credit" ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                  {it.type === "credit" ? "+" : "−"} {formatPKR(it.actual ?? it.amount)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top dues */}
        <div className="border border-[#E3E3E3] bg-white" data-testid="dashboard-top-dues">
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#E3E3E3]">
            <div>
              <div className="overline mb-1">Outstanding Dues</div>
              <h3 className="font-display text-lg font-semibold text-[#0F172A]">Top 5 by amount</h3>
            </div>
            <Link to="/reports" data-testid="dashboard-view-dues-link" className="text-xs uppercase tracking-wider font-semibold text-[#0F172A] border-b border-[#0F172A]">
              Full report →
            </Link>
          </div>
          <div className="divide-y divide-[#E3E3E3]" data-testid="top-dues-list">
            {topDues.length === 0 && (
              <div className="px-6 py-12 text-center text-sm text-[#6B7280]" data-testid="top-dues-empty">
                No outstanding payables or receivables. All settled.
              </div>
            )}
            {topDues.map((d, i) => (
              <div key={d.party_id || `none-${i}`} className="px-6 py-4 flex items-center justify-between" data-testid={`dues-row-${i}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-1 h-10 ${d.net >= 0 ? "bg-[#2563EB]" : "bg-[#EF4444]"}`} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#111827] truncate">{d.name}</div>
                    <div className="text-xs text-[#6B7280]">{d.entries_count} entries · {d.type || "—"}</div>
                  </div>
                </div>
                <div className="text-right shrink-0 ml-3">
                  {d.receivable > 0 && (
                    <div className="font-mono text-xs text-[#2563EB] font-semibold">R: {formatPKR(d.receivable)}</div>
                  )}
                  {d.payable > 0 && (
                    <div className="font-mono text-xs text-[#EF4444] font-semibold">P: {formatPKR(d.payable)}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
